import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import React, { useState } from 'react';

import { toSxArray } from '_lib/utils/helpers';

import { DebounceOptions, useDebouncedOnChange } from '../../hooks/useDebouncedOnChange';

import { IconButton } from '../IconButton/IconButton';
import { TextField, TextFieldProps } from '../TextField/TextField';

export type SearchProps = Omit<TextFieldProps, 'onChange'> & {
  /**
   * @default false
   */
  disableSearchOnEnter?: boolean;
  /**
   * @default false
   */
  disableSearchOnChange?: boolean;
  onSearch: (value: string) => void;
  /**
   * @default undefined
   */
  initialValue?: DebounceOptions['initialValue'];
  millis?: DebounceOptions['millis'];
};

export const Search = (props: SearchProps) => {
  const { disableSearchOnEnter, disableSearchOnChange, onSearch, initialValue, millis, sx, ...rest } = props;
  const { text, setText, onChangeDebounced } = useDebouncedOnChange(onSearch, { initialValue, millis });
  const handleOnChange = disableSearchOnChange ? (value: string) => setText(value) : onChangeDebounced;
  const handleOnEnter = disableSearchOnEnter ? undefined : () => onSearch(text);

  // This is needed to support the parent component changing the 'initialValue' without re-rendering
  const [lastInitialValue, setLastInitialValue] = useState(initialValue);
  if (initialValue !== undefined && lastInitialValue !== initialValue) {
    setLastInitialValue(initialValue);
    setText(initialValue);
  }

  return (
    <TextField
      placeholder='Search...'
      {...rest}
      onEnter={handleOnEnter}
      onChange={(e) => handleOnChange(e.target.value)}
      value={text}
      slotProps={{
        input: {
          'aria-label': 'search',
          startAdornment: <SearchIcon sx={(theme) => ({ color: theme.palette.primary.main })} />,
          endAdornment: (
            <>
              {text && (
                <IconButton
                  size='small'
                  onClick={() => {
                    handleOnChange('');
                  }}>
                  <CloseIcon />
                </IconButton>
              )}
            </>
          )
        }
      }}
      sx={[
        ...toSxArray(sx),
        // This keeps the input from re-sizing when the 'close' icon is present (it has 5px padding)
        { minWidth: text ? '250px' : '255px' },
        {
          '& .MuiInputBase-input.MuiOutlinedInput-input': {
            paddingLeft: 1
          },
          '& .MuiInputBase-root.MuiOutlinedInput-root': {
            paddingRight: 1
          }
        }
      ]}
    />
  );
};
